import Axios, { AxiosResponse } from 'axios';

import { CustomerBasicResponse, CustomerSearchForm, CrmListCustomersResponse } from '../types/person';
import {
  CrmLoginForm,
  CrmTokenResponse,
  ScanStatistics,
} from '../types/crm';
import { DataPass } from '../types/dataPassport';
import { MerchantOfferCreate, MerchantOfferEdit, Offer } from '../types/offer';
import { EventComponent, EventRole, EventSlug } from '../constants/events';
import { PaymentStart, TotalBalance } from '../types/stripe';

import { ApprovedMerchantsResponse, MerchantSignupT, SimpleMerchantSignup } from '../types/merchant';
import { Transaction } from '../types/transactions';
import { uidToHash2 } from '../utils/hash';
import { getIssuer } from './pda';
import { config } from '../configs/config';

const apiPrefix = config.apiPrefix;

function mkHeaders(token: string) {
  return {
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };
}

function mkUploadHeaders(token: string, onProgress: (progressEvent: any) => void) {
  return {
    headers: {
      'content-type': 'form/multipart',
      accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    onUploadProgress: onProgress,
  };
}


// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const uploadCsvFile = (
  file: File,
  token: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUploadProgress: (progressEvent: any) => void,
) => {
  const formData = new FormData();

  formData.append('customers_file', file);

  return Axios.post(`${apiPrefix}/api/crm/customers/upload`, formData, {
    onUploadProgress,
  });
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const listCrmCustomers = (token: string, page = 1, pageCount = 10) =>
  Axios.get<CrmListCustomersResponse[]>(
    `${apiPrefix}/api/crm/customers?page=${page}&page_count=${pageCount}`,
    mkHeaders(token),
  );

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const crmLogin = ({ username, password }: CrmLoginForm) => {
  const formData = new FormData();
  formData.append('username', username);
  formData.append('password', password);
  return Axios.post<CrmTokenResponse>(`${apiPrefix}/api/auth/jwt/login`, formData, {
    headers: { 'content-type': 'multipart/form-data' },
  });
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getCustomerFirstLogin = (token: string) =>
  Axios.get<{ value: boolean }>(`${apiPrefix}/api/customer/check-first-login`, mkHeaders(token));

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types  
export const getCustomerBasic = (token: string, passId: string) =>
  Axios.get<CustomerBasicResponse>(`${apiPrefix}/api/customer/data/${passId}/basic`, mkHeaders(token));

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types 
export const searchCustomer = (token: string, data: CustomerSearchForm, passId: string) =>
  Axios.post<CrmListCustomersResponse[]>(
    `${apiPrefix}/api/customer/data/${passId}/search`,
    data,
    mkHeaders(token),
  );

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types  
export const claimCustomerData = (token: string, data: { id: string }, passId: string) =>
  Axios.post<CrmListCustomersResponse>(
    `${apiPrefix}/api/customer/data/${passId}/claim`,
    data,
    mkHeaders(token),
  );

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types  
export const activatePass = (token: string, body: string, passId: string) =>
  Axios.post<CrmListCustomersResponse>(
    `${apiPrefix}/api/customer/data/${passId}/claim`,
    body,
    mkHeaders(token),
  );


// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types  
export const getStatistics = async (dataPassId: string, token: string) =>
  Axios.get<ScanStatistics>(
    `${apiPrefix}/api/merchant/data-passes/${dataPassId}/scan-transactions-count?interval_days=7`,
    mkHeaders(token),
  );

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types  
export const getCustomerStatistics = async (dataPassId: string, token: string) =>
  Axios.get<ScanStatistics>(
    `${apiPrefix}/api/customer/data-passes/${dataPassId}/scan-transactions-count?interval_days=7`,
    mkHeaders(token),
  );

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types  
export const merchantEmailVerification = (password: string, token: string) =>
  Axios.post<CrmTokenResponse>(
    `${apiPrefix}/api/auth/create-password`,
    { password, token },
    {
      headers: { 'content-type': 'application/json' },
    },
  );

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const merchantResetPassword = (password: string, token: string) =>
  Axios.post(
    `${apiPrefix}/api/auth/reset-password`,
    { password, token },
    {
      headers: { 'content-type': 'application/json' },
    },
  );

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types  
export const merchantForgotPassword = (email: string) =>
  Axios.post(
    `${apiPrefix}/api/auth/forgot-password`,
    { email },
    {
      headers: { 'content-type': 'application/json' },
    },
  );

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types  
export const verifyBarcode = (dataPassId: string, barcode: string, token: string) =>
  Axios.post<{ verified: boolean }>(
    `${apiPrefix}/api/merchant/data-passes/${dataPassId}/barcode/verify`,
    { barcode },
    mkHeaders(token),
  );

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types  
export const getMerchantDataPasses = async (token: string) =>
  Axios.get<DataPass[]>(`${apiPrefix}/api/merchant/data-passes`, mkHeaders(token));

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types  
export const getDataPasses = async (token: string) =>
  Axios.get<DataPass[]>(`${apiPrefix}/api/customer/data-passes`, mkHeaders(token));

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types  
export const getOffers = async (token: string) =>
  Axios.get<Offer[]>(`${apiPrefix}/api/customer/offers`, mkHeaders(token));

// Stats
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export async function recordEvent(
  role: EventRole,
  token: string,
  component: EventComponent | string,
  componentId: string,
  slug: EventSlug,
) {
  const issuer = getIssuer(token);

  if (issuer) {
    uidToHash2(issuer).then((digest: string) => {
      return Axios.put<string>(
        `${apiPrefix}/api/${role}/events/${component}/${componentId}/${slug}?uid=${digest.toString()}}`,
        {},
        mkHeaders(token),
      )
    })
  }
  else {
    return Axios.put<string>(
      `${apiPrefix}/api/${role}/events/${component}/${componentId}/${slug}`,
      {},
      mkHeaders(token)
    )
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export async function recordCustomerEvent(
  token: string,
  component: EventComponent,
  componentId: string,
  slug: EventSlug,
) {
  return (recordEvent(EventRole.CUSTOMER, token, component, componentId, slug));
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const recordMerchantEvent = (
  token: string,
  component: EventComponent,
  componentId: string,
  slug: EventSlug,
) => recordEvent(EventRole.MERCHANT, token, component, componentId, slug);

// Merchant Offers CRUD

// CREATE
export async function createOffer(
  token: string,
  offer: MerchantOfferCreate
): Promise<AxiosResponse<string>> {
  return (Axios.post<string>(
    `${apiPrefix}/api/merchant/offers`,
    offer,
    mkHeaders(token),
  ));
}

// READ
export async function getMerchantOffers(
  token: string
): Promise<AxiosResponse<MerchantOfferEdit[]>> {
  return (Axios.get<MerchantOfferEdit[]>(
    `${apiPrefix}/api/merchant/offers`,
    mkHeaders(token),
  ));
}


// UPDATE
export async function updateOffer(
  token: string,
  offer: MerchantOfferEdit
): Promise<AxiosResponse<string>> {
  return (Axios.put<string>(
    `${apiPrefix}/api/merchant/offers/${offer.id}`,
    offer,
    mkHeaders(token),
  ));
}

// Update Status
export async function updateOfferStatus(
  token: string,
  offer: MerchantOfferEdit,
  status: string
): Promise<AxiosResponse<string>> {
  return (Axios.put<string>(
    `${apiPrefix}/api/merchant/offers/${offer.id}/update-status/${status}`,
    {},
    mkHeaders(token),
  ));
}


// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const uploadOfferImage = (
  file: File,
  token: string,
  offer_id: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUploadProgress: (progressEvent: any) => void,
) => {
  const formData = new FormData();

  formData.append('image_file', file);

  return Axios.post(
    `${apiPrefix}/api/merchant/offers/${offer_id}/upload`,
    formData,
    mkUploadHeaders(token, onUploadProgress));
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const uploadOfferImageNoCallback = (
  file: File,
  token: string,
  offer_id: string
) => {
  const formData = new FormData();

  formData.append('image_file', file);

  return Axios.post(
    `${apiPrefix}/api/merchant/offers/${offer_id}/upload`,
    formData,
    mkHeaders(token));
};


export const startPayment = (
  token: string,
  amount: number
): Promise<AxiosResponse<PaymentStart>> => {
  return Axios.post<PaymentStart>(
    `${apiPrefix}/api/merchant/payment/start`,
    { "amount": amount },
    mkHeaders(token),
  )
};

export const getTotalBalance = (
  token: string,
): Promise<AxiosResponse<TotalBalance>> => {
  return Axios.get<TotalBalance>(
    `${apiPrefix}/api/merchant/total-balance`,
    mkHeaders(token),
  )
};


export const signupMerchant = (
  body: SimpleMerchantSignup
): Promise<AxiosResponse<any>> => {
  return Axios.post<any>(
    `${apiPrefix}/api/merchant/signup`,
    body,
    { headers: { 'content-type': 'application/json' } }
  )
}

export const createMerchant = (
  token: string,
  body: MerchantSignupT
): Promise<AxiosResponse<any>> => {
  return Axios.put<any>(
    `${apiPrefix}/api/merchant/update`,
    body,
    mkHeaders(token),
  )
}

// Tags
export const getTags = (
  token: string,
): Promise<AxiosResponse<string[]>> => {
  return Axios.get<string[]>(
    `${apiPrefix}/api/merchant/offer/tags`,
    mkHeaders(token),
  )
}

// Scan Transactions
export const syncMerchantTransactions = (
  token: string,
  data: string[]
): Promise<AxiosResponse<Transaction[]>> => {
  return Axios.post<Transaction[]>(
    `${apiPrefix}/api/customer/scan-transactions/diff`,
    data,
    mkHeaders(token),
  )
}

// Approved Merchants
// POST / api / customer / data / { data_pass_id } / claim
export const getApprovedMerchants = (
  token: string,
  dataPassId: string,
): Promise<AxiosResponse<ApprovedMerchantsResponse>> => {
  return Axios.post<ApprovedMerchantsResponse>(
    `${apiPrefix}/api/customer/data/${dataPassId}/claim`,
    {},
    mkHeaders(token),
  )
}

// Simple Store for Sign-up /api/stores/{key}

export const getStoreData = (
  token: string, key: string
): Promise<AxiosResponse<any>> => {
  return Axios.get<any>(
    `${apiPrefix}/api/stores/${key}`,
    mkHeaders(token),
  )
}

export const postStoreData = (
  token: string, key: string, body: string
): Promise<AxiosResponse<any>> => {
  return Axios.post<any>(
    `${apiPrefix}/api/stores/${key}`,
    body,
    mkHeaders(token),
  )
}

export const deleteStoreData = (
  token: string,
  key: string,
): Promise<AxiosResponse<any>> => {
  return Axios.delete<any>(
    `${apiPrefix}/api/stores/${key}`,
    mkHeaders(token),
  )
}